/// <amd-module name="Test/TestPlansLibrary/TestCasesPivotFilter/Redux/TestCasesPivotFilterSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";

import { ContentViewObserverActions, ContentViewObserverActionTypes } from "VSS/Redux/Modules/ContentViewObserver";
import { IVssPageContext } from "VSS/Platform/Context";
import { getCurrentFilterState } from "VSS/Redux/Modules/ContentViewObserver";
import { format } from "VSS/Core/Util/String";

import { IIdentity } from "VSSUI/IdentityPicker";
import { IFilterState, IFilterItemState } from "VSSUI/Utilities/Filter";

import {
    getDataProviderContentAsync,
    createWorkItemDataProviderState,
    getDataProviderContextContract
} from "Test/TestPlansLibrary/LibraryHub/Utils/TestPlansLibraryUtils";
import { LibraryQueryConstants, WorkItemConstants } from "Test/TestPlansLibrary/LibraryHub/Constants";
import { ILibraryWorkItemDataProviderState } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryContracts";

import * as Contracts from "TestPlanCommon/Contracts/TestPlan";

import { getWorkItemDataProviderStateFromState } from "Test/TestPlansLibrary/Pivots/TestCasesPivot/Redux/TestCasesSelector";
import { TestCasesActions } from "Test/TestPlansLibrary/Pivots/TestCasesPivot/Redux/TestCasesActions";

import { TastCasesPivotFilterActions, TestCasesPivotFilterActionTypes } from "./TestCasesPivotFilterActions";
import { getAssignedToSelectionFromState } from "./TestCasesPivotFilterSelector";
import * as Constants from "../TestCasesPivotFilterConstants";

export function* TestCasesPivotFilterSaga(): SagaIterator {
    // Assigned to filter actions binding
    yield effects.takeLatest(TestCasesPivotFilterActionTypes.addAssignedToIdentity, addAssignedToIdentity);
    yield effects.takeLatest(TestCasesPivotFilterActionTypes.removeAssignedToIdentity, removeAssignedToIdentity);
    yield effects.takeLatest(TestCasesPivotFilterActionTypes.removeAssignedToIdentities, removeAssignedToIdentities);

    // FilterContext actions binding
    yield effects.takeLatest(ContentViewObserverActionTypes.FilterStateChanged, evaluateTestCasesFilter);

    yield effects.takeLatest(TestCasesPivotFilterActionTypes.applyFilters, applyFilters);
}

/**
 * Saga used when an identity is added in assigned to filter
 */
export function* addAssignedToIdentity(
    action: ActionsOfType<TastCasesPivotFilterActions, TestCasesPivotFilterActionTypes.addAssignedToIdentity>
): SagaIterator {
    let currentIdentities: IIdentity[] = (<unknown>(yield effects.select(getAssignedToSelectionFromState))) as IIdentity[];

    currentIdentities.push(action.payload.identity);

    yield effects.put(TastCasesPivotFilterActions.updateAssignedToIdentities(currentIdentities));

    // Apply the filters
    yield effects.put(TastCasesPivotFilterActions.applyFilters());
}

/**
 * Saga used when an identity is removed from assigned to filter
 */
export function* removeAssignedToIdentity(
    action: ActionsOfType<TastCasesPivotFilterActions, TestCasesPivotFilterActionTypes.removeAssignedToIdentity>
): SagaIterator {
    let currentIdentities: IIdentity[] = (<unknown>(yield effects.select(getAssignedToSelectionFromState))) as IIdentity[];

    currentIdentities = currentIdentities.filter(identity => identity.entityId !== action.payload.identity.entityId);

    yield effects.put(TastCasesPivotFilterActions.updateAssignedToIdentities(currentIdentities));

    // Apply the filters
    yield effects.put(TastCasesPivotFilterActions.applyFilters());
}

/**
 * Saga used when multiple identities are removed from assigned to filter
 */
export function* removeAssignedToIdentities(
    action: ActionsOfType<TastCasesPivotFilterActions, TestCasesPivotFilterActionTypes.removeAssignedToIdentities>
): SagaIterator {
    let currentIdentities: IIdentity[] = (<unknown>(yield effects.select(getAssignedToSelectionFromState))) as IIdentity[];

    currentIdentities = currentIdentities.filter(
        currentIdentity => action.payload.identities.filter(actionIdentity => currentIdentity.entityId === actionIdentity.entityId).length === 0
    );

    yield effects.put(TastCasesPivotFilterActions.updateAssignedToIdentities(currentIdentities));

    // Apply the filters
    yield effects.put(TastCasesPivotFilterActions.applyFilters());
}

/**
 * Saga that is called when state of filters is changed.
 */
export function* evaluateTestCasesFilter(
    action: ActionsOfType<ContentViewObserverActions, ContentViewObserverActionTypes.FilterStateChanged>
): SagaIterator {
    // Apply the filters
    yield effects.put(TastCasesPivotFilterActions.applyFilters());
}

export function* applyFilters(): SagaIterator {
    // Default to all test cases query
    let queriesFilterString: string = LibraryQueryConstants.QueryAllTestCasesKey;

    // If action payload contains queries filter key, extract it
    const currentFilterState: IFilterState | undefined = yield effects.select(getCurrentFilterState);
    if (currentFilterState && currentFilterState[Constants.TestCasesQueriesFilterItemKey]) {
        const queriesFilterValue: IFilterItemState | null = currentFilterState[Constants.TestCasesQueriesFilterItemKey];
        if (queriesFilterValue !== null) {
            queriesFilterString = (queriesFilterValue as IFilterItemState).value[0];
        }
    }

    let assignedToFilterValues: string[] = [];
    // Get the assigned to identities for filtering
    const assignedToIdentites: IIdentity[] = (<unknown>(yield effects.select(getAssignedToSelectionFromState))) as IIdentity[];
    if (assignedToIdentites.length > 0) {
        assignedToIdentites.forEach((identity: IIdentity) => {
            assignedToFilterValues.push(format("{0} <{1}>", identity.displayName, identity.mail));
        });
    }

    let providerState: ILibraryWorkItemDataProviderState | undefined = yield effects.select(getWorkItemDataProviderStateFromState);

    if (providerState) {
        providerState = providerState as ILibraryWorkItemDataProviderState;
        // Reset the continuationToken and hasMoreElements
        providerState.continuationToken = undefined;
        providerState.hasMoreElements = false;

        if (providerState.queryType !== queriesFilterString) {
            providerState.queryType = queriesFilterString;
        }

        providerState.filterValues = [];

        if (assignedToFilterValues.length > 0) {
            providerState.filterValues.push({
                fieldName: WorkItemConstants.WitAssignedToField,
                fieldValues: assignedToFilterValues,
                filterMode: Contracts.TestPlansLibraryWorkItemFilterMode.Or
            });
        }

        yield effects.put(TestCasesActions.updateProviderState(providerState));

        const providerContextContract: Contracts.LibraryWorkItemsDataProviderRequest = (<unknown>(yield effects.call(
            getDataProviderContextContract,
            providerState
        ))) as Contracts.LibraryWorkItemsDataProviderRequest;

        const pageContext: IVssPageContext = (<unknown>(yield effects.getContext("pageContext"))) as IVssPageContext;

        yield effects.put(TestCasesActions.clearTestCasesAndSetLoadingExperience());

        let data: Contracts.LibraryWorkItemsData | undefined = yield effects.call(getDataProviderContentAsync, pageContext, providerContextContract);

        const dataProviderState: ILibraryWorkItemDataProviderState = (<unknown>(yield effects.call(createWorkItemDataProviderState, data, providerState.queryType))) as ILibraryWorkItemDataProviderState;

        if (data) {
            data = data as Contracts.LibraryWorkItemsData;

            yield effects.put(TestCasesActions.loadInitialTestCasesSucceeded(data.workItems, dataProviderState));
        }
    }
}
